import { queryClient } from '@/lib/react-query';
import { GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY } from '@/features/cases/case/api/get-customer-profile-from-case.api';
import { TCaseCustomerProfile } from '@/features/case/_shared/types/case-customer-profile.type';
import { TAssignLabelCaseValidation } from '../../../api/assign-label-case.api';
import { TLabelCase } from '../../../types/label-case.type';
import { AxiosErrorCustom } from '@/lib/axios';
import { axiosExtractErrorMessage } from '@/lib/axios';

export type TAssignLabelCaseOptimisticContext = {
  previousData?: { payload: TCaseCustomerProfile };
};

export interface IAssignLabelCaseOptimisticUpdateParams {
  caseId?: string;
  allAvailableLabels: TLabelCase[];
}

/**
 * Optimistic update handler for assign label case mutation
 * Updates the case profile cache immediately when labels are assigned
 */
export const createAssignLabelCaseOptimisticUpdate = ({
  caseId,
  allAvailableLabels,
}: IAssignLabelCaseOptimisticUpdateParams) => {
  return {
    onMutate: async (variables: TAssignLabelCaseValidation): Promise<TAssignLabelCaseOptimisticContext> => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      if (caseId) {
        await queryClient.cancelQueries({
          queryKey: [GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId],
        });

        // Snapshot the previous value for rollback
        const previousData = queryClient.getQueryData<{
          payload: TCaseCustomerProfile;
        }>([GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId]);

        // Optimistically update the cache using updater function to ensure correct structure
        if (previousData?.payload) {
          // Map labelIds to full label objects
          const updatedLabelCases = variables.labelIds
            .map((labelId: string) => {
              const label = allAvailableLabels.find((l: TLabelCase) => l.labelCaseId === labelId);
              return label
                ? {
                    labelCaseId: label.labelCaseId,
                    name: label.name,
                    color: label.color,
                  }
                : null;
            })
            .filter((label): label is { labelCaseId: string; name: string; color: string } => label !== null);

          queryClient.setQueryData<{ payload: TCaseCustomerProfile }>(
            [GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId],
            (old) => {
              if (!old?.payload) return old;
              return {
                ...old,
                payload: {
                  ...old.payload,
                  labelCases: updatedLabelCases,
                },
              };
            }
          );
        }

        return { previousData };
      }
      return {};
    },

    onError: (
      error: AxiosErrorCustom,
      _variables: TAssignLabelCaseValidation,
      context: TAssignLabelCaseOptimisticContext | undefined
    ) => {
      // Rollback to previous data on error
      if (caseId && context?.previousData) {
        queryClient.setQueryData([GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId], context.previousData);
      }

      return {
        errorMessage: axiosExtractErrorMessage(error) || 'Failed to assign case labels',
      };
    },

    onSuccess: () => {
      // Don't invalidate immediately - let the optimistic update stay visible
      // The data will sync on the next natural refetch or when the component remounts
      // This prevents the "delayed" feeling by keeping the optimistic update visible
    },
  };
};
